/**
 * Floor Selector Tool
 *
 * Allows selection of pre-detected floors from the objects tab.
 * Loads floors from Floor groups in the project state, retrieving
 * floor/ceiling properties to reconstruct floor data.
 */

import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig } from '../../types';
import { FloorToolBase } from './FloorToolBase';
import { DetectedFloorPlane, visualizeDetectedFloors } from '../../../utils/floorDetection';
import { createMultiViewConfig, ViewModes } from '../../helpers';
import { loadFloorDetectionArea } from '@pointorama/potree-shared';
import * as THREE from 'three';
import { Utils } from '../../../utils';
import { FloorplanPersona, FloorGroupData } from '../../personas/implementations/FloorplanPersona';
import { ContextMenu, ContextMenuItem, ICON_REFRESH_SVG } from '../modules/contextMenu/ContextMenu';
import { PointoramaOutline } from '../../../utils/PointoramaOutline';
import { buildLayoutWallSketchForFloor } from '../../../utils/skeleton/buildLayoutWallSketch';
import { resolveFloorOutlineRings } from '../../../utils/skeleton/outlineRings';

export class FloorSelectorTool extends FloorToolBase {
  readonly name = 'floor_selector';
  private floorGroupsData: FloorGroupData[] = [];

  // World-space anchored context menu for floor right-click actions.
  private contextMenu: ContextMenu | null = null;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);

    // Re-enable double-click zoom for this tool (base class disables it)
    if (this.viewer.orbitControls) {
      this.viewer.orbitControls.doubleClickZoomEnabled = true;
    }
  }

  protected onEnable(): void {
    super.onEnable();

    // Load saved detection area corners from project properties
    try {
      const corners = loadFloorDetectionArea();
      if (corners) {
        this.detectionAreaCorners = corners.map((c) => new THREE.Vector3(c.x, c.y, c.z));
      }
    } catch {
      // Property service may not be initialized yet, fall back to scene bounds
    }

    // Ensure double-click zoom is enabled when tool is activated
    if (this.viewer.orbitControls) {
      this.viewer.orbitControls.doubleClickZoomEnabled = true;
    }

    // Listen for floor data changes from persona
    this.persona.addEventListener('floor_data_changed', this.onFloorDataChanged);
    this.persona.addEventListener('floor_selection_changed', this.onFloorSelectionChanged);

    // Listen for hover events from hierarchy
    this.viewer.scene.addEventListener('floor_group_hover_from_hierarchy', this.onGroupHoveredFromHierarchy);

    // Get initial floor data from persona (may not be available yet on first enable)
    const floorplanPersona = this.persona as FloorplanPersona;
    const initialFloorData = floorplanPersona.getFloorGroupsData();
    if (initialFloorData.length > 0) {
      this.setFloorGroupsData(initialFloorData);

      // Show preview for currently selected floor if any
      const selectedFloorData = floorplanPersona.getSelectedFloorData();
      if (selectedFloorData) {
        // Find the floor index in availableFloors using the floor plane height
        const floorIndex = this.availableFloors.findIndex((floorLevel) => floorLevel.floor.height === selectedFloorData.floor.height);

        if (floorIndex !== -1) {
          // Preview this floor using the floor and ceiling planes from the persona
          super.previewFloor(floorIndex, selectedFloorData.floor, selectedFloorData.ceiling || undefined);
        }
      }
    } else {
      // No data yet - try loading from cache, events will handle updates
      this.loadFloorsFromGroups();
    }
  }

  protected onDisable(): void {
    super.onDisable();

    // Remove listeners
    this.persona.removeEventListener('floor_data_changed', this.onFloorDataChanged);
    this.persona.removeEventListener('floor_selection_changed', this.onFloorSelectionChanged);
    this.viewer.scene.removeEventListener('floor_group_hover_from_hierarchy', this.onGroupHoveredFromHierarchy);

    this.contextMenu?.hide();
  }

  /**
   * Override to allow double-click pan (don't prevent propagation)
   */
  public onDoubleClick(_event: MouseEvent): void {
    // Allow event to propagate to input listeners for camera pan
    // Don't call event.preventDefault() or stopPropagation()
  }

  /**
   * Handle floor data changed event from persona
   */
  private onFloorDataChanged = (event: { floorGroupsData: FloorGroupData[] }) => {
    const floorGroupsData = event.floorGroupsData || [];
    this.setFloorGroupsData(floorGroupsData);

    // After setting floor data, check if there's a selected floor to preview
    const floorplanPersona = this.persona as FloorplanPersona;
    const selectedFloorData = floorplanPersona.getSelectedFloorData();
    if (selectedFloorData && this.availableFloors.length > 0) {
      const floorIndex = this.availableFloors.findIndex((floorLevel) => floorLevel.floor.height === selectedFloorData.floor.height);

      // Re-preview even when a preview is already active: setFloorGroupsData
      // just reset the clip to the resting state, so the previewed floor's
      // slab clip and outline must be re-applied (with the fresh planes).
      if (floorIndex !== -1) {
        super.previewFloor(floorIndex, selectedFloorData.floor, selectedFloorData.ceiling || undefined);
      }
    }
  };

  /**
   * Handle floor selection changed event from persona
   */
  private onFloorSelectionChanged = (event: { selectedFloorGroupIdentifier: string | null; floorData: { floor: DetectedFloorPlane; ceiling?: DetectedFloorPlane | null } | null }) => {
    const selectedFloorGroupIdentifier = event.selectedFloorGroupIdentifier;
    const floorData = event.floorData;

    // If no floor is selected, cancel preview
    if (!selectedFloorGroupIdentifier || !floorData) {
      if (this.previewFloorIndex !== -1) {
        super.cancelPreview();
      }
      return;
    }

    // Match by group identifier — availableFloors mirrors floorGroupsData order.
    // Height matching would fail when this event carries refreshed dimensions
    // (persona plane subscription), as our own floor list still holds the old
    // heights until the next floor_data_changed.
    const floorIndex = this.floorGroupsData.findIndex((group) => group.groupIdentifier === selectedFloorGroupIdentifier);

    if (floorIndex !== -1) {
      // Preview this floor using the floor and ceiling planes from the event
      super.previewFloor(floorIndex, floorData.floor, floorData.ceiling ?? undefined);
    }
  };

  /**
   * Handle when a floor group is hovered from the hierarchy panel
   * This temporarily highlights the floor in the 3D view
   */
  private onGroupHoveredFromHierarchy = (event: { value: { groupIdentifier: string; isHovering: boolean } }) => {
    const { groupIdentifier, isHovering } = event.value || {};
    if (!groupIdentifier) return;

    if (!isHovering) {
      // Stop hovering - clear the hover state
      this.hoveredFloorIndex = -1;
      this.updateFloorVisualization();

      // Restore the preview of the currently selected floor if any
      if (this.previewFloorIndex !== -1) {
        const selectedFloorLevel = this.availableFloors[this.previewFloorIndex];
        const selectedFloorGroup = this.floorGroupsData.find((group) => group.floor.height === selectedFloorLevel.floor.height);
        if (selectedFloorGroup) {
          super.previewFloor(this.previewFloorIndex, selectedFloorGroup.floor, selectedFloorGroup.ceiling);
        }
      }
      return;
    }

    // Find the floor group to get the actual floor and ceiling planes
    const floorGroup = this.floorGroupsData.find((group) => group.groupIdentifier === groupIdentifier);

    if (!floorGroup) return;

    // Find the floor level index in availableFloors using the floor plane
    const floorIndex = this.availableFloors.findIndex((floorLevel) => floorLevel.floor.height === floorGroup.floor.height);

    if (floorIndex !== -1) {
      // Set as hovered and update visualization
      this.hoveredFloorIndex = floorIndex;
      this.updateFloorVisualization();
    }
  };

  /**
   * Request floor data from the project state. Floor groups arrive via the
   * persona's `floor_data_changed` event → `setFloorGroupsData`.
   */
  private loadFloorsFromGroups(): void {
    this.viewer.scene.dispatchEvent({
      type: 'request_floor_data',
      message: 'Floor selector tool needs floor data from groups',
    });

    // Show a message to the user
    this.dispatchEvent({
      type: 'no_floors_found',
      message: 'No cached floor data found. Please run Auto Floor detection first, or load from saved Floor groups.',
    });
  }

  /**
   * Set floors from floor groups loaded from project properties
   * Called by the client application after loading floor group data
   * @param floorGroups - Array of floor group data with floor and ceiling planes
   */
  public setFloorGroupsData(floorGroups: FloorGroupData[]): void {
    const newFloorIdentifiers = new Set((floorGroups || []).map((g) => g.groupIdentifier));

    // Check if the currently previewed floor was deleted
    if (this.previewFloorIndex !== -1 && this.floorGroupsData.length > 0) {
      const previewedFloorGroup = this.floorGroupsData.find((group) => group.floor.height === this.availableFloors[this.previewFloorIndex]?.floor.height);
      if (previewedFloorGroup && !newFloorIdentifiers.has(previewedFloorGroup.groupIdentifier)) {
        // The previewed floor was deleted, cancel preview
        this.cancelPreview();
      }
    }

    if (!floorGroups || floorGroups.length === 0) {
      // Clear existing data and visualization when all floors are deleted
      this.floorGroupsData = [];
      this.detectedFloors = [];
      this.availableFloors = [];
      this.clearFloorVisualization();

      this.dispatchEvent({
        type: 'no_floors_found',
        message: 'No floor groups found in project.',
      });
      return;
    }

    // Check if there are actual changes
    // Store floor groups data for later retrieval
    this.floorGroupsData = floorGroups;

    // Build detectedFloors array: all planes (floor + ceiling) for visualization
    const allPlanes: DetectedFloorPlane[] = [];

    floorGroups.forEach((group) => {
      // Add floor plane
      allPlanes.push(group.floor);

      // Add ceiling plane if it exists
      if (group.ceiling) {
        allPlanes.push(group.ceiling);
      }
    });

    // Sort all planes by height
    allPlanes.sort((a, b) => a.height - b.height);

    // Store all planes for visualization
    this.detectedFloors = allPlanes;

    // availableFloors: floor/ceiling pairs (DetectedFloorLevel[])
    // Convert floor groups to floor levels. Legacy persisted groups stored
    // the ceiling AT the next floor's height — stamp them 'next-floor' so
    // consumers can distinguish them from true-ceiling detections. Each
    // floor's OWN persisted footprint drives its visualization and box
    // clipping (fallback: global detection rect).
    this.availableFloors = floorGroups.map((group) => ({
      name: group.name,
      floor: group.floor,
      ceiling: group.ceiling || null,
      ceilingKind: 'next-floor' as const,
      footprintCorners: group.footprintCorners?.map((c) => new THREE.Vector2(c.x, c.y)),
    }));

    // Visualize all planes (debug visualization)
    visualizeDetectedFloors(this.viewer, allPlanes);

    this.dispatchEvent({
      type: 'floors_loaded',
      floors: this.detectedFloors,
    });

    this.createFloorVisualization();
  }

  /**
   * Override previewFloor to select the floor group in the hierarchy
   * and complete the selection immediately (no confirmation needed)
   */
  protected previewFloor(floorIndex: number): void {
    if (floorIndex < 0 || floorIndex >= this.availableFloors.length) {
      return;
    }

    // Find the floor group that matches this floor level
    const selectedFloorLevel = this.availableFloors[floorIndex];
    const floorGroup = this.floorGroupsData.find((group) => group.floor.height === selectedFloorLevel.floor.height);

    if (floorGroup) {
      // Dispatch event to select the floor group in the hierarchy
      this.viewer.scene.dispatchEvent({
        type: 'select_group',
        value: {
          groupIdentifier: floorGroup.groupIdentifier,
        },
      });
    }

    // Call parent preview to show the floor in 3D
    super.previewFloor(floorIndex);
  }

  /**
   * Bar clicks mirror this tool's 3D click semantics: preview/select only —
   * the selector has no confirmation step.
   */
  protected onFloorBarClicked(floorIndex: number): void {
    this.previewFloor(floorIndex);
  }

  /**
   * No confirmation step: the floors are already persisted groups (plain
   * {x,y,z} normals, not Vector3), so the base Enter-to-confirm must not run.
   */
  protected confirmFloorSelection(_floorIndex: number): void {
    // Intentionally empty.
  }

  /**
   * Override cancelPreview to deselect the floor group in hierarchy
   */
  protected cancelPreview(): void {
    // Dispatch event to deselect in hierarchy
    this.viewer.scene.dispatchEvent({
      type: 'deselect_all',
      value: {},
    });

    // Call parent to hide preview
    super.cancelPreview();
  }

  /**
   * Override click handler to only preview (no confirmation needed)
   */
  /** Selection tool: highlight + isolate on selection, but no box editing. */
  protected allowsPreviewEditing(): boolean {
    return false;
  }

  protected handleFloorClick(event: MouseEvent): void {
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const camera = this.viewer.scene.getActiveCamera();
    this.raycaster.setFromCamera(this.mouse, camera);

    // If we're in preview mode, check if we clicked on point cloud
    if (this.previewFloorIndex !== -1) {
      // Check if we hit any visible (non-clipped) point cloud
      // Use Utils.getMousePointCloudIntersection with pickClipped: false to ignore hidden points
      // Convert to pixel coordinates (Utils expects pixel coords, not NDC)
      const mousePixel = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
      const intersection = Utils.getMousePointCloudIntersection(mousePixel, camera, this.viewer, this.viewer.scene.pointclouds, {
        pickClipped: false, // Ignore clipped/hidden points
        all: false,
      });

      // Only cancel preview if clicking on empty space (not on visible point cloud)
      if (!intersection) {
        this.cancelPreview();
      }
      return;
    }

    const intersects = this.raycaster.intersectObjects(this.floorMeshes, false);

    if (intersects.length > 0) {
      const floorIndex = intersects[0].object.userData.floorIndex;

      if (floorIndex !== undefined) {
        // Only preview, no confirmation step
        this.previewFloor(floorIndex);
      }
    } else {
      // Clicking elsewhere cancels preview
      if (this.previewFloorIndex !== -1) {
        this.cancelPreview();
      }
    }
  }

  /**
   * Right-click a floor to open a context menu offering to generate a wall
   * skeleton ("wall sketch") for that floor from its rooms. If the click
   * doesn't land on a floor, fall back to the base behaviour (cancel preview).
   */
  public onRightClick(event: MouseEvent): void {
    event.preventDefault();

    const hit = this.pickFloorGroupAt(event);
    if (!hit) {
      this.contextMenu?.hide();
      super.onRightClick(event);
      return;
    }

    // Merged sketch outline first, legacy OUTLINE annotation fallback. The
    // legacy annotation id (when present) is linked in the sketch metadata.
    const rings = resolveFloorOutlineRings(this.viewer, hit.groupIdentifier);
    const legacyOutline = this.viewer.scene.outlines.find((o) => (o as PointoramaOutline).groupIdentifier === hit.groupIdentifier) as PointoramaOutline | undefined;

    // Without an outline there is no building footprint to seed a skeleton
    // from, so the action would no-op — skip the menu in that case.
    if (rings.length === 0) {
      this.contextMenu?.hide();
      return;
    }

    if (!this.contextMenu) {
      this.contextMenu = new ContextMenu(this.viewer);
    }

    const items: ContextMenuItem[] = [
      {
        id: 'generate-wall-sketch',
        iconSvg: ICON_REFRESH_SVG,
        tooltip: this.viewer.translationService.t('generate wall sketch'),
        closeOnClick: true,
        onClick: () => {
          void buildLayoutWallSketchForFloor(this.viewer, hit.groupIdentifier, hit.floorHeight, rings, legacyOutline?.identifier ?? null).catch((err) => {
            console.error(`Generate wall sketch failed for floor ${hit.groupIdentifier}:`, err);
          });
        },
      },
    ];

    this.contextMenu.show(items, hit.anchor);
  }

  /**
   * Raycast the floor meshes under the cursor and resolve the floor group,
   * its floor height, and a world anchor for the context menu. Returns null
   * when the cursor isn't over a floor.
   */
  private pickFloorGroupAt(event: MouseEvent): { groupIdentifier: string; floorHeight: number; anchor: THREE.Vector3 } | null {
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const camera = this.viewer.scene.getActiveCamera();
    this.raycaster.setFromCamera(this.mouse, camera);

    const intersects = this.raycaster.intersectObjects(this.floorMeshes, false);
    if (intersects.length === 0) return null;

    const floorIndex = intersects[0].object.userData.floorIndex as number | undefined;
    if (floorIndex === undefined) return null;

    const floorLevel = this.availableFloors[floorIndex];
    if (!floorLevel) return null;

    const floorGroup = this.floorGroupsData.find((group) => group.floor.height === floorLevel.floor.height);
    if (!floorGroup) return null;

    return {
      groupIdentifier: floorGroup.groupIdentifier,
      floorHeight: floorGroup.floor.height,
      anchor: intersects[0].point.clone(),
    };
  }

  /** Floor-creation mode keeps the inherited box clip; other modes use fixed-height isolation. */
  private isInFloorCreationMode(): boolean {
    return (this.persona as FloorplanPersona).getCurrentMode()?.id === 'floor_select';
  }

  /** Floor group identifier for a floor plane, matched by height. */
  private findFloorGroupId(floor: DetectedFloorPlane): string | null {
    return this.floorGroupsData.find((group) => group.floor.height === floor.height)?.groupIdentifier ?? null;
  }

  /** Isolate a hovered/previewed floor: box clip while creating floors, fixed-height otherwise. */
  protected isolateFloor(floor: DetectedFloorPlane, ceiling: DetectedFloorPlane, corners?: THREE.Vector2[]): void {
    if (this.isInFloorCreationMode()) {
      super.isolateFloor(floor, ceiling, corners);
      return;
    }
    const floorGroupId = this.findFloorGroupId(floor);
    if (floorGroupId) {
      this.applyFixedHeightIsolation(floorGroupId);
    }
  }

  /** Resting-state isolation: all-floors box while creating floors, the selected floor otherwise. */
  protected isolateDefault(): void {
    if (this.isInFloorCreationMode()) {
      super.isolateDefault();
      return;
    }
    const selectedFloorGroupId = (this.persona as FloorplanPersona).getSelectedFloorGroupIdentifier();
    if (selectedFloorGroupId) {
      this.applyFixedHeightIsolation(selectedFloorGroupId);
    } else {
      this.clearBoxIsolation();
    }
  }

  /**
   * Provide custom view configuration
   */
  getViewConfig(): IViewConfig {
    return createMultiViewConfig([ViewModes.PERSPECTIVE_3D_RGB], ViewModes.PERSPECTIVE_3D_RGB.id);
  }
}
